import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Wrench, DollarSign, Activity, Download, FileSpreadsheet, X, Users, UserCog, Wallet, Receipt, FileText, TrendingUp, BadgeDollarSign, Car, Globe } from "lucide-react";
import { toast } from "sonner";
import { QuotesSummaryReport, QuotesDetailedReport, QUOTE_STATUSES } from "@/components/reports/QuotesReport";
import { BusinessHealthReport } from "@/components/reports/BusinessHealthReport";
import { PaymentsReport, PAYMENT_METHOD_OPTIONS } from "@/components/reports/PaymentsReport";
import { ExpensesReport, ScheduleCReport, ProfitLossReport, Vendors1099Report, MileageReport, OnlineBookingsReport } from "@/components/reports/ExpensesReport";
import { InvoiceSummaryReport, InvoiceDetailedReport, WorkOrderSummaryReport, WorkOrderDetailedReport } from "@/components/reports/ReportTables";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORTS = [
  { id: "business-health", label: "Business Health", icon: Activity, group: "health", pdf: "business-health/pdf", detailed: false },
  { id: "payments", label: "Payments", icon: Wallet, group: "payments", pdf: "payments/pdf", detailed: false, csv: true },
  { id: "expenses", label: "Expenses", icon: Receipt, group: "expenses", pdf: "expenses/pdf", detailed: false, csv: true },
  { id: "schedule-c", label: "Schedule C", icon: FileText, group: "expenses", pdf: "schedule-c", detailed: false, csv: true, yearly: true },
  { id: "profit-loss", label: "Profit & Loss", icon: TrendingUp, group: "expenses", pdf: null, detailed: false },
  { id: "1099-vendors", label: "1099 Vendors", icon: BadgeDollarSign, group: "expenses", pdf: null, detailed: false, csv: true, yearly: true },
  { id: "mileage", label: "Mileage", icon: Car, group: "expenses", pdf: null, detailed: false, csv: true },
  { id: "online-bookings", label: "Online Bookings", icon: Globe, group: "bookings", pdf: null, detailed: false },
  { id: "invoice-by-customer", label: "Invoices by Customer", icon: Users, group: "invoice", pdf: "invoice-by-customer/pdf" },
  { id: "invoice-by-technician", label: "Invoices by Technician", icon: UserCog, group: "invoice", pdf: null },
  { id: "workorder-by-customer", label: "Work Orders by Customer", icon: Wrench, group: "workorder", pdf: "workorder-by-customer/pdf" },
  { id: "workorder-by-technician", label: "Work Orders by Technician", icon: Wrench, group: "workorder", pdf: null },
  { id: "quotes-by-customer", label: "Quotes by Customer", icon: DollarSign, group: "quotes", pdf: "quotes-by-customer/pdf" },
];
const DETAIL_ENDPOINT = { invoice: "invoices-by-status", workorder: "workorders-by-status", quotes: "quotes-by-status", payments: "payments", expenses: "expenses" };
const INVOICE_STATUSES = ["pending", "sent", "paid", "partial"];
const WORK_STATUSES = ["open", "closed"];
const EMPTY = { customer_id: "", technician_id: "", status: "", method: "", start_date: "", end_date: "", period: "all" };

const iso = (d) => d.toISOString().split("T")[0];
const periodRange = (period) => {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  if (period === "this-month") return [iso(new Date(y, m, 1)), iso(new Date(y, m + 1, 0))];
  if (period === "last-month") return [iso(new Date(y, m - 1, 1)), iso(new Date(y, m, 0))];
  if (period === "this-quarter") { const q = Math.floor(m / 3) * 3; return [iso(new Date(y, q, 1)), iso(new Date(y, q + 3, 0))]; }
  if (period === "this-year") return [iso(new Date(y, 0, 1)), iso(new Date(y, 11, 31))];
  if (period === "last-90") { const d = new Date(now); d.setDate(d.getDate() - 90); return [iso(d), iso(now)]; }
  return ["", ""];
};

const saveBlob = (res, fallback) => {
  const match = /filename=([^;]+)/.exec(res.headers["content-disposition"] || "");
  const url = window.URL.createObjectURL(res.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1].trim() : fallback;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

export default function Reports() {
  const [reportType, setReportType] = useState("business-health");
  const [viewMode, setViewMode] = useState("summary");
  const [filters, setFilters] = useState(EMPTY);
  const [customers, setCustomers] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const report = REPORTS.find((r) => r.id === reportType);
  const isDetailed = viewMode === "detailed" && report.detailed !== false;
  const byTechnician = reportType.includes("technician");

  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(r.data)).catch(() => {});
    axios.get(`${API}/technicians`).then((r) => setTechnicians(r.data)).catch(() => {});
  }, []);

  const params = useCallback((extra = {}) => {
    const p = new URLSearchParams();
    if (filters.customer_id) p.append("customer_id", filters.customer_id);
    if (filters.technician_id) p.append("technician_id", filters.technician_id);
    if (filters.status) p.append("status", filters.status);
    if (filters.method && report.group === "payments") p.append("method", filters.method);
    if (filters.start_date) p.append("start_date", filters.start_date);
    if (filters.end_date) p.append("end_date", filters.end_date);
    if (report.yearly) p.append("year", (filters.start_date || filters.end_date || new Date().toISOString()).slice(0, 4));
    Object.entries(extra).forEach(([k, v]) => v && p.append(k, v));
    return p.toString();
  }, [filters, report.group, report.yearly]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setData(null);
    try {
      const endpoint = isDetailed ? DETAIL_ENDPOINT[report.group] : reportType;
      const res = await axios.get(`${API}/reports/${endpoint}?${params()}`);
      setData(res.data);
    } catch (error) {
      toast.error("Failed to load report");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [isDetailed, report.group, reportType, params]);

  useEffect(() => { fetchReport(); }, [reportType, viewMode]);

  const selectReport = (id) => {
    setReportType(id);
    setFilters((f) => ({ ...f, status: "", method: "" }));
    if (REPORTS.find((r) => r.id === id).detailed === false) setViewMode("summary");
  };
  const set = (patch) => setFilters({ ...filters, ...patch });
  const setPeriod = (period) => { const [s, e] = periodRange(period); setFilters({ ...filters, period, start_date: s, end_date: e }); };
  const clear = () => { setFilters(EMPTY); setTimeout(fetchReport, 50); };

  const download = async (kind) => {
    setBusy(kind);
    try {
      let url, fallback;
      if (kind === "csv") { url = `${API}/reports/${report.group === "expenses" ? reportType : DETAIL_ENDPOINT[report.group]}?${params({ format: "csv" })}`; fallback = `${reportType}.csv`; }
      else if (kind === "pdf" && report.yearly) { url = `${API}/reports/${report.pdf}?${params({ format: "pdf" })}`; fallback = `${reportType}.pdf`; }
      else if (kind === "pdf-detail") { url = `${API}/reports/invoices-detailed/pdf?${params({ group_by: byTechnician || filters.technician_id ? "technician" : "customer" })}`; fallback = "invoice-detail-report.pdf"; }
      else { url = `${API}/reports/${report.pdf}?${params()}`; fallback = `${reportType}-report.pdf`; }
      const res = await axios.get(url, { responseType: "blob" });
      saveBlob(res, fallback);
      toast.success("Report downloaded");
    } catch (error) {
      toast.error("Download failed");
    } finally {
      setBusy(null);
    }
  };

  const statusOptions = report.group === "quotes" ? QUOTE_STATUSES.map((s) => s.value) : report.group === "workorder" ? WORK_STATUSES : INVOICE_STATUSES;
  const showStatus = report.group !== "health" && report.group !== "payments" && report.group !== "expenses" && (isDetailed || report.group === "quotes");
  const showMethod = report.group === "payments";
  const showCustomer = report.group !== "health";
  const showTechnician = report.group !== "health" && report.group !== "payments";
  const activeFilters = Object.entries(filters).filter(([k, v]) => k !== "period" && v).length;

  const renderBody = () => {
    if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" /></div>;
    if (!data) return null;
    if (report.group === "health") return Array.isArray(data) ? null : <BusinessHealthReport data={data} />;
    if (report.group === "payments") return Array.isArray(data) || !data.payments ? null : <PaymentsReport data={data} />;
    if (reportType === "expenses") return <ExpensesReport data={data} />;
    if (reportType === "schedule-c") return <ScheduleCReport data={data} />;
    if (reportType === "profit-loss") return <ProfitLossReport data={data} />;
    if (reportType === "1099-vendors") return <Vendors1099Report data={data} />;
    if (reportType === "mileage") return <MileageReport data={data} />;
    if (reportType === "online-bookings") return <OnlineBookingsReport data={data} />;
    if (!Array.isArray(data)) return null;
    if (report.group === "quotes") return isDetailed ? <QuotesDetailedReport quotes={data} /> : <QuotesSummaryReport data={data} />;
    if (report.group === "invoice") return isDetailed ? <InvoiceDetailedReport invoices={data} /> : <InvoiceSummaryReport data={data} byTechnician={byTechnician} />;
    return isDetailed ? <WorkOrderDetailedReport entries={data} /> : <WorkOrderSummaryReport data={data} byTechnician={byTechnician} />;
  };

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white shadow-lg">
        <h1 className="text-3xl font-bold">Reports</h1>
        <p className="text-blue-100 mt-1">Business health, billing, work orders and quotes — filterable, sortable, exportable.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="report-type-selector">
        {REPORTS.map((r) => (
          <Button key={r.id} variant={reportType === r.id ? "default" : "outline"} className="h-20 flex-col gap-2 whitespace-normal" onClick={() => selectReport(r.id)} data-testid={`report-type-${r.id}`}>
            <r.icon className="w-5 h-5" />
            <span className="text-xs leading-tight">{r.label}</span>
          </Button>
        ))}
      </div>

      <Card className="bg-white border-0 shadow-lg" data-testid="report-filters">
        <CardContent className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-4">
            {showCustomer && (
              <div>
                <Label>Customer</Label>
                <Select value={filters.customer_id || "all"} onValueChange={(v) => set({ customer_id: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="report-customer-filter"><SelectValue placeholder="All Customers" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Customers</SelectItem>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showTechnician && (
              <div>
                <Label>{report.group === "quotes" ? "Created By (Technician)" : "Technician"}</Label>
                <Select value={filters.technician_id || "all"} onValueChange={(v) => set({ technician_id: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="report-technician-filter"><SelectValue placeholder="All Technicians" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Technicians</SelectItem>{technicians.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showStatus && (
              <div>
                <Label>Status</Label>
                <Select value={filters.status || "all"} onValueChange={(v) => set({ status: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="report-status-filter"><SelectValue placeholder="All Statuses" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Statuses</SelectItem>{statusOptions.map((s) => <SelectItem key={s} value={s}>{s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {showMethod && (
              <div>
                <Label>Payment Method</Label>
                <Select value={filters.method || "all"} onValueChange={(v) => set({ method: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="report-method-filter"><SelectValue placeholder="All Methods" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All Methods</SelectItem>{PAYMENT_METHOD_OPTIONS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Period</Label>
              <Select value={filters.period} onValueChange={setPeriod}>
                <SelectTrigger data-testid="report-period-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="this-month">This Month</SelectItem>
                  <SelectItem value="last-month">Last Month</SelectItem>
                  <SelectItem value="this-quarter">This Quarter</SelectItem>
                  <SelectItem value="last-90">Last 90 Days</SelectItem>
                  <SelectItem value="this-year">This Year</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>From</Label><Input type="date" value={filters.start_date} onChange={(e) => set({ start_date: e.target.value, period: "custom" })} data-testid="report-start-date" /></div>
            <div><Label>To</Label><Input type="date" value={filters.end_date} onChange={(e) => set({ end_date: e.target.value, period: "custom" })} data-testid="report-end-date" /></div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button onClick={fetchReport} className="bg-blue-600 hover:bg-blue-700" data-testid="apply-filters-button">Apply Filters</Button>
            {activeFilters > 0 && <Button variant="ghost" onClick={clear} data-testid="clear-filters-button"><X className="w-4 h-4 mr-1" /> Clear ({activeFilters})</Button>}
          </div>
        </CardContent>
      </Card>

      <Card className="bg-white border-0 shadow-lg">
        <CardHeader className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <report.icon className="w-5 h-5 text-blue-600" /> {report.label}
            {isDetailed && Array.isArray(data) && <Badge variant="outline" data-testid="detailed-record-count">{data.length} records</Badge>}
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {report.detailed !== false && (
              <div className="flex rounded-lg border border-slate-200 overflow-hidden" data-testid="view-mode-toggle">
                <button className={`px-3 py-1.5 text-sm ${viewMode === "summary" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} onClick={() => setViewMode("summary")} data-testid="view-summary-button">Summary</button>
                <button className={`px-3 py-1.5 text-sm ${viewMode === "detailed" ? "bg-blue-600 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`} onClick={() => setViewMode("detailed")} data-testid="view-detailed-button">Detailed</button>
              </div>
            )}
            {isDetailed && report.group === "invoice" && (
              <Button size="sm" onClick={() => download("pdf-detail")} disabled={!!busy} className="bg-green-600 hover:bg-green-700" data-testid="download-pdf-button"><Download className="w-4 h-4 mr-1" /> {busy === "pdf-detail" ? "Preparing…" : "PDF with Totals"}</Button>
            )}
            {!isDetailed && report.pdf && (
              <Button size="sm" onClick={() => download("pdf")} disabled={!!busy} className="bg-green-600 hover:bg-green-700" data-testid="download-pdf-button"><Download className="w-4 h-4 mr-1" /> {busy === "pdf" ? "Preparing…" : "Download PDF"}</Button>
            )}
            {(isDetailed || report.csv) && (
              <Button size="sm" variant="outline" onClick={() => download("csv")} disabled={!!busy} data-testid="download-csv-button"><FileSpreadsheet className="w-4 h-4 mr-1" /> {busy === "csv" ? "Preparing…" : "CSV with Totals"}</Button>
            )}
          </div>
        </CardHeader>
        <CardContent>{renderBody()}</CardContent>
      </Card>
    </div>
  );
}
